/**
 * Where the two kinds of credential live in the browser, and how a partner's
 * one-click link hands one over.
 *
 * localStorage, not a cookie: the panel and the API are different origins, so
 * a cookie set here would never be sent there anyway. Every request carries
 * its token in an Authorization header explicitly (see api.ts).
 *
 * THE ONE-CLICK LINK. A partner is given
 * `https://referral.solex.games/#key=<their key>`.
 *
 * THE FRAGMENT, NOT A QUERY STRING, and the difference is the whole reason
 * this comment is long. A `?key=` would be sent to the server on that first
 * navigation and recorded verbatim in Cloudflare's HTTP access logs for the
 * panel — anyone who can read those logs would hold working credentials for
 * every partner who ever opened their link. A fragment is never transmitted:
 * the browser strips it before the request leaves. There is no server-side
 * fix for the query-string version, which is why the key does not travel
 * that way at all.
 *
 * On first load `claimKeyFromUrl` moves the key into storage and rewrites the
 * address bar without it, so it also stops appearing in the tab title, in
 * screen shares, and in whatever the partner pastes into a chat later.
 * index.html's `no-referrer` covers the Referer header in the meantime.
 *
 * It is still a bearer secret in a link, which means anyone the partner
 * forwards it to becomes them. That is an accepted trade for a login with no
 * password and no email round-trip; rotating the key from the admin panel is
 * the revocation path, and it is one click.
 */

const ADMIN_TOKEN_KEY = "solex_panel_admin_token";
const PARTNER_KEY = "solex_panel_partner_key";

/** Guarded so the module is importable in a non-browser test environment. */
function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    // Safari in private mode throws on access rather than returning null.
    return null;
  }
}

function read(key: string): string | null {
  return storage()?.getItem(key)?.trim() || null;
}

function write(key: string, value: string | null): void {
  const store = storage();
  if (!store) return;
  if (value) store.setItem(key, value);
  else store.removeItem(key);
}

export const adminToken = {
  get: () => read(ADMIN_TOKEN_KEY),
  set: (token: string | null) => write(ADMIN_TOKEN_KEY, token),
};

export const partnerKey = {
  get: () => read(PARTNER_KEY),
  set: (key: string | null) => write(PARTNER_KEY, key),
};

/**
 * Picks `#key=…` out of the URL fragment, stores it, and strips it from the
 * address bar. Returns the claimed key, or null when there was none.
 *
 * `replaceState` rather than `pushState`: the key-bearing URL must not remain
 * as a history entry the back button can return to.
 *
 * A `?key=` in the query string is deliberately NOT accepted. Supporting it
 * "just in case someone sends one" would mean the leaky form kept working,
 * and a link that quietly succeeds is a link people keep making.
 */
export function claimKeyFromUrl(): string | null {
  if (typeof window === "undefined") return null;

  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return null;

  // Parsed as a parameter list so the fragment stays extensible, rather than
  // assuming it contains nothing but the key.
  const key = new URLSearchParams(hash).get("key")?.trim();
  if (!key) return null;

  partnerKey.set(key);
  const { pathname, search } = window.location;
  window.history.replaceState(null, "", `${pathname}${search}`);
  return key;
}

/** Clears both credentials. Used by the sign-out control on either view. */
export function signOut(): void {
  adminToken.set(null);
  partnerKey.set(null);
}
